"use client";
import React, { useState } from "react";

function CurriculumStructure({ course }) {
  const [activeSemester, setActiveSemester] = useState(0);

  if (!course?.CurriculumStructure || course.CurriculumStructure.length === 0) {
    return null;
  }

  const currentSemester = course.CurriculumStructure[activeSemester];

  // Extract text from rich text format
  const extractText = (richText) => {
    if (!richText || !Array.isArray(richText)) return "";
    return richText
      .map(
        (block) =>
          block.children?.map((child) => child.text || "").join("") || ""
      )
      .join(" ");
  };

  // Extract list items from rich text format with support for bold text
  const extractListItems = (richText) => {
    if (!richText || !Array.isArray(richText)) return [];
    const listBlock = richText.find((block) => block.type === "list");
    if (!listBlock) return [];

    return listBlock.children.map((item) => {
      // Return an array of text segments with their formatting
      return item.children?.map((child) => ({
        text: child.text || "",
        bold: child.bold || false
      })) || [];
    });
  };

  // Render text with bold formatting
  const renderFormattedText = (segments) => {
    if (!Array.isArray(segments)) return null;

    return segments.map((segment, idx) => (
      segment.bold ? (
        <strong key={idx}>{segment.text}</strong>
      ) : (
        <span key={idx}>{segment.text}</span>
      )
    ));
  };

  return (
    <div className="mb-[50px] md:mb-[80px] xl:mb-[100px] page-container">
      {/* Header */}
      <div className=" mb-[40px] xl:mb-[48px]">
        <h2 className="w-full lg:w-[60%]">Curriculum Structure</h2>
        <p className="w-full md:w-[80%] xl:w-[60%]">
          Step into a future-focused learning environment with industry-oriented programmes designed to build practical skills, real-world readiness, and broad career opportunities across disciplines
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar - Horizontal on mobile/tablet, Vertical on desktop */}
        <div className="pl-[16px] md:pl-[32px] xl:pl-[0px] w-full lg:w-64 lg:flex-shrink-0 flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-3 lg:gap-0 pb-4 lg:pb-0">
          {course.CurriculumStructure.map((semester, index) => (
            <button
              key={semester.id || index}
              onClick={() => setActiveSemester(index)}
              className={`flex-shrink-0 lg:w-full min-w-[160px] text-left px-[14px] py-[16px] lg:px-[20px] lg:py-[22px] lg:mb-[18px] border transition-all whitespace-nowrap ${activeSemester === index
                  ? "border-[#00304B] text-[#00304B] font-semibold shadow-custom-blue"
                  : "bg-white border-gray-200"
                }`}
            >
              <span
                className="flex items-center justify-between text-[16px] xl:text-[20px] tracking-[0.5px]"
                style={{ fontFamily: "saans-regular" }}
              >
                {semester.SemesterNumber}
                <span className="text-xl">+</span>
              </span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-[#EDF6FC] rounded-lg p-6 lg:p-8">
          {/* Semester Label */}
          <div className="text-[#EB9100] font-semibold bg-[#FFF0D7] inline-block px-[10px] py-[3px] mb-[14px]">
            {currentSemester.SemesterNumber}
          </div>

          {/* Heading */}
          <h3
            className="mb-[13px] text-2xl lg:text-[30px]"
            style={{ fontFamily: "saans-medium" }}
          >
            {currentSemester.SemesterHeading}
          </h3>

          {/* Description */}
          <p
            className="mb-[31px] w-full lg:w-[75%]"
            style={{
              fontFamily: "saans-regular",
              fontSize: "16px",
              lineHeight: "25px",
            }}
          >
            {extractText(currentSemester.SemesterDescription)}
          </p>

          {/* Key Subjects */}
          {currentSemester.SemesterSubjects && (
            <div className="mb-[40px]">
              <h4
                className="text-[18px] font-medium mb-[16px]"
                style={{ fontFamily: "saans-bold" }}
              >
                Key Subjects
              </h4>
              <div className="flex flex-wrap gap-3">
                {extractListItems(currentSemester.SemesterSubjects).map(
                  (subject, index) => (
                    <span
                      key={index}
                      className="bg-white px-[16px] py-[10px] rounded-full text-[16px] leading-[22px]"
                    >
                      {renderFormattedText(subject)}
                    </span>
                  )
                )}
              </div>
            </div>
          )}

          {/* Learning Outcomes */}
          {currentSemester.SemesterOutcomes && (
            <div>
              <h4
                className="text-[18px] mb-[20px]"
                style={{ fontFamily: "saans-bold" }}
              >
                Learning Outcomes
              </h4>
              <div className="">
                {extractListItems(currentSemester.SemesterOutcomes).map(
                  (outcome, index) => (
                    <div key={index} className="flex gap-[12px] items-start lg:items-center mb-[16px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        className="flex-shrink-0 mt-1 lg:mt-0"
                      >
                        <path
                          d="M8.16284 15.7788C8.09162 13.9723 7.34155 12.259 6.0632 10.9806C4.78485 9.70217 3.07108 8.95265 1.26535 8.88125L0 8.50442L1.30861 8.15382C3.10713 8.08954 4.8155 7.35048 6.09385 6.08367C7.3713 4.81686 8.12587 3.11534 8.2061 1.31754L8.61809 0L8.89847 1.21242C8.95527 3.02889 9.69811 4.75628 10.9783 6.04625C12.2584 7.33614 13.9803 8.09243 15.796 8.16256L17 8.50442L15.7437 8.84618C13.9361 8.91551 12.2224 9.66449 10.9431 10.9433C9.66476 12.2222 8.9156 13.9366 8.84618 15.7438L8.50451 17L8.16284 15.7788Z"
                          fill="black"
                        />
                      </svg>
                      <span className="text-[16px] leading-[21px]">
                        {renderFormattedText(outcome)}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CurriculumStructure;